/**
 * GateAnnotationNotice — the machine check that fired, and the sentence it objected to.
 *
 * WHY THIS EXISTS (WI-ONE-POST-REACHES-THE-CEO-001 Leg 4).
 *
 * The gate ruling of 2026-08-01 — "A machine gate may ANNOTATE a draft. It may not kill one.
 * The seat decides." — was built into the council in PR #1341: every failing check now attaches
 * a `gate_annotations` entry to the draft naming WHICH check fired, WHAT it says in English, and
 * WHICH SENTENCE tripped it. The draft then reaches the CEO instead of dying.
 *
 * None of it rendered. The backend wrote the record and no surface read it, so for the one
 * person it was written for it did not exist. That is what this component is: the read side.
 *
 * THE HONESTY RULE IT INHERITS, and must not quietly break.
 * `_build_gate_annotation` refuses to leave "which sentence?" blank. Where a check STRUCTURALLY
 * cannot produce one — a word count is a property of the whole draft, not of any sentence — it
 * writes `trigger_span_absent_reason` saying so IN WORDS. An empty span is indistinguishable
 * from a check that never bothered to look, and that ambiguity is precisely what made the
 * 2026-07-31 Delhi EV death impossible to argue with. So this component renders the
 * absent-reason with the same weight as a quote. It never renders an empty block, and it never
 * silently omits an annotation it cannot fully describe.
 *
 * NO NEW DESIGN. Colours come from DESIGN_INTENTS, spacing and type from DS_*, labels from
 * task-display-config. Type is at or above the 16px floor (CLAUDE.md rule 13) because the CEO
 * reads this surface by text-to-speech and residual vision.
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DESIGN_INTENTS, DS_COLORS, DS_SPACING, DS_TYPOGRAPHY } from '@/config/ui-invariants';
import {
    LINKEDIN_GATE_ANNOTATION_LABELS as LABELS,
    LINKEDIN_GATE_ANNOTATION_LABELS_COPY as COPY,
    AGENT_CONFIG,
} from '@/config/task-display-config';
import { repairAnnotation, overruleAnnotation } from '@/lib/api';

// WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — the verb labels/copy for Repair and Overrule
// live in the SAME labels home as every other Post Studio card string (never inline).
const CARD_LABELS = AGENT_CONFIG.post_studio?.labels || {};
const VERB_LABELS = CARD_LABELS.verbButtonLabels || {};
const OUTCOME_LABELS = CARD_LABELS.reEvaluationOutcomeLabels || {};

/**
 * One annotation is renderable when it can answer at least one of the two questions the record
 * exists to answer: what the check said, or which sentence it fired on. An entry that can answer
 * neither is a producer gap — it is dropped rather than rendered as an empty amber box, and the
 * count in the heading reflects what is actually shown.
 */
function isRenderable(a) {
    if (!a || typeof a !== 'object') return false;
    return Boolean(a.reason || a.trigger_span || a.trigger_span_absent_reason || a.failure_class);
}

/**
 * The reason sentence with its machine prefix removed.
 *
 * FOUND BY THE TEST FOR THIS COMPONENT, not by reading the backend. Every `reason` the council
 * writes begins with its own failure class — `"stance_narration: a sentence narrates the
 * author's own watching..."`, `"wordcount: the draft is 322 words against..."`. Those strings
 * were written as TELEMETRY, and the annotate ruling promoted them to CEO-facing copy without
 * anyone re-reading them as copy. CLAUDE.md rules 17-18: no tech jargon visible to the user,
 * ever.
 *
 * Stripped HERE rather than rewritten in the backend deliberately. The prefix is load-bearing
 * on the machine side — `termination_reason` roll-ups and several tests match on it — so
 * changing it there would be a wide edit to fix a narrow display problem. It is also pure
 * duplication on screen: the chip beside it already says "Voice".
 *
 * Only an EXACT `<failure_class>:` prefix is removed. Anything else is left untouched, because
 * silently truncating a reason we did not recognise would be a worse failure than showing it.
 */
function humanReason(a) {
    const reason = a.reason || '';
    const cls = a.failure_class;
    if (!reason || !cls) return reason;
    const prefix = `${cls}:`;
    if (!reason.startsWith(prefix)) return reason;
    return reason.slice(prefix.length).trim();
}

/**
 * AnnotationVerbControls — Repair and Overrule, rendered ONLY when the server's
 * available_verbs says they are legal for THIS card (WI-APPROVED-IDEA-BECOMES-A-DRAFT-003).
 *
 * The component decides nothing about legality — it renders exactly the buttons it is
 * handed and nothing else. `annotationIndex` is the index into the CARD's own
 * gate_annotations array (the caller passes the ORIGINAL index, not a post-filter one —
 * see `originalIndex` in GateAnnotationNotice below), because that is the index the
 * annotation-repair / annotation-overrule endpoints key on.
 *
 * OVERRULE never hides the annotation it was recorded against — the disagreement is the
 * data. This component only ever ADDS a note beside the existing block; the parent list
 * that renders the quoted sentence above is untouched by anything that happens here.
 */
function AnnotationVerbControls({ itemId, annotationIndex, verbs, span }) {
    const [mode, setMode] = useState(null); // null | 'repair' | 'overrule'
    const [replacement, setReplacement] = useState(span || '');
    const [words, setWords] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [repairOutcome, setRepairOutcome] = useState(null);
    const [overruled, setOverruled] = useState(false);

    const canRepair = Boolean(itemId) && Array.isArray(verbs) && verbs.includes('repair');
    const canOverrule = Boolean(itemId) && Array.isArray(verbs) && verbs.includes('overrule');

    const handleRepairSubmit = useCallback(async () => {
        if (!replacement.trim()) {
            setError(CARD_LABELS.repairReplacementRequired || 'Write a replacement before saving.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const result = await repairAnnotation(itemId, annotationIndex, span || '', replacement, true);
            setRepairOutcome(result?.re_evaluation || null);
            setMode(null);
        } catch (err) {
            // Backend error honesty needs the frontend link — an honest message, never a
            // silent no-op (CLAUDE.md "no tech jargon", and the estate's standing rule
            // against swallowing a failed call).
            setError(err?.message || CARD_LABELS.repairError || 'Could not save that fix — please try again.');
        } finally {
            setBusy(false);
        }
    }, [replacement, itemId, annotationIndex, span]);

    const handleOverruleSubmit = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            await overruleAnnotation(itemId, annotationIndex, words || null);
            setOverruled(true);
            setMode(null);
        } catch (err) {
            setError(err?.message || CARD_LABELS.overruleError || 'Could not record that — please try again.');
        } finally {
            setBusy(false);
        }
    }, [itemId, annotationIndex, words]);

    if (!canRepair && !canOverrule) return null;

    const btnStyle = {
        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
        minHeight: DS_SPACING.minTouchTarget + 'px',
    };

    return (
        <div style={{ marginTop: '12px' }} data-testid="gate-annotation-verb-controls">
            {overruled && (
                <p
                    data-testid="gate-annotation-overruled-note"
                    role="note"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.secondary,
                        fontStyle: 'italic',
                        marginBottom: '8px',
                    }}
                >
                    {CARD_LABELS.overruledNote || 'You chose to keep this as written.'}
                </p>
            )}

            {repairOutcome && (
                <p
                    data-testid="gate-annotation-repair-outcome"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.primary,
                        marginBottom: '8px',
                    }}
                >
                    {OUTCOME_LABELS[repairOutcome.outcome] || CARD_LABELS.unknownOutcome
                        || 'We got a result we don’t recognise yet — nothing was lost, but please check back on this card.'}
                    {Number.isFinite(repairOutcome.retry_count) && (
                        <> {CARD_LABELS.reEvaluateRetryCount
                            ? CARD_LABELS.reEvaluateRetryCount(repairOutcome.retry_count)
                            : `Tried ${repairOutcome.retry_count} times.`}</>
                    )}
                </p>
            )}

            {error && (
                <p
                    data-testid="gate-annotation-verb-error"
                    role="alert"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DESIGN_INTENTS.negative.text,
                        marginBottom: '8px',
                    }}
                >
                    {error}
                </p>
            )}

            {mode === 'repair' ? (
                <div>
                    <label
                        style={{
                            display: 'block',
                            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                            color: DS_COLORS.text.secondary,
                            marginBottom: '4px',
                        }}
                    >
                        {CARD_LABELS.repairFieldLabel || 'Your replacement wording'}
                    </label>
                    <textarea
                        data-testid="gate-annotation-repair-input"
                        value={replacement}
                        onChange={(e) => setReplacement(e.target.value)}
                        disabled={busy}
                        className="w-full bg-white rounded-lg border border-slate-200 p-2"
                        style={{ ...btnStyle, minHeight: '80px' }}
                    />
                    <div className="flex items-center gap-2" style={{ marginTop: '8px' }}>
                        <button
                            type="button"
                            data-testid="gate-annotation-repair-submit"
                            disabled={busy}
                            onClick={handleRepairSubmit}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.primary.bg} ${DESIGN_INTENTS.primary.text} ${DESIGN_INTENTS.primary.border}`}
                            style={btnStyle}
                        >
                            {CARD_LABELS.repairSubmitLabel || 'Save and check again'}
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => { setMode(null); setError(''); }}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                            style={btnStyle}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : mode === 'overrule' ? (
                <div>
                    <label
                        style={{
                            display: 'block',
                            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                            color: DS_COLORS.text.secondary,
                            marginBottom: '4px',
                        }}
                    >
                        {CARD_LABELS.overruleFieldLabel || 'Say why, if you want to (optional)'}
                    </label>
                    <textarea
                        data-testid="gate-annotation-overrule-input"
                        value={words}
                        onChange={(e) => setWords(e.target.value)}
                        disabled={busy}
                        className="w-full bg-white rounded-lg border border-slate-200 p-2"
                        style={{ ...btnStyle, minHeight: '60px' }}
                    />
                    <div className="flex items-center gap-2" style={{ marginTop: '8px' }}>
                        <button
                            type="button"
                            data-testid="gate-annotation-overrule-submit"
                            disabled={busy}
                            onClick={handleOverruleSubmit}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.primary.bg} ${DESIGN_INTENTS.primary.text} ${DESIGN_INTENTS.primary.border}`}
                            style={btnStyle}
                        >
                            {VERB_LABELS.overrule || 'Keep it anyway'}
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => { setMode(null); setError(''); }}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                            style={btnStyle}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    {canRepair && (
                        <button
                            type="button"
                            data-testid="gate-annotation-repair-button"
                            disabled={busy}
                            onClick={() => { setMode('repair'); setError(''); }}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.warning.bg} ${DESIGN_INTENTS.warning.text} ${DESIGN_INTENTS.warning.border}`}
                            style={btnStyle}
                        >
                            {VERB_LABELS.repair || 'Fix this line'}
                        </button>
                    )}
                    {canOverrule && !overruled && (
                        <button
                            type="button"
                            data-testid="gate-annotation-overrule-button"
                            disabled={busy}
                            onClick={() => { setMode('overrule'); setError(''); }}
                            className={`px-3 rounded-lg border font-semibold ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                            style={btnStyle}
                        >
                            {VERB_LABELS.overrule || 'Keep it anyway'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export function GateAnnotationNotice({ annotations, itemId = null, availableVerbs = [] }) {
    // originalIndex is preserved through the filter deliberately: annotation_index sent to
    // the repair/overrule endpoints must key into the CARD's own gate_annotations array,
    // not a post-filter position — a dropped, non-renderable entry ahead of a renderable
    // one would otherwise silently misdirect an action onto the wrong annotation.
    const items = (Array.isArray(annotations) ? annotations : [])
        .map((a, originalIndex) => ({ a, originalIndex }))
        .filter((entry) => isRenderable(entry.a));
    if (items.length === 0) return null;

    const intent = DESIGN_INTENTS.warning;

    return (
        <div
            className={`rounded-xl border ${intent.bg} ${intent.border}`}
            style={{ padding: DS_SPACING.cardPadding + 'px', marginBottom: '16px' }}
            data-testid="gate-annotation-notice"
        >
            <p
                className="font-bold"
                style={{
                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                    color: DS_COLORS.text.primary,
                    marginBottom: '4px',
                }}
            >
                {items.length > 1 ? COPY.headingPlural : COPY.heading}
            </p>
            {/* The ruling, said out loud on the surface. Without this line an amber box reads as
                a rejection, which is the exact veto the ruling removed. */}
            <p
                style={{
                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                    color: DS_COLORS.text.secondary,
                    marginBottom: '12px',
                }}
            >
                {COPY.subheading}
            </p>

            {items.map(({ a, originalIndex }, i) => {
                const mapped = LABELS[a.failure_class];
                const span = a.trigger_span;
                const absent = a.trigger_span_absent_reason;
                const reason = humanReason(a);
                return (
                    <div
                        key={`${a.failure_class || 'check'}-${originalIndex}`}
                        style={{ marginTop: i === 0 ? 0 : '16px' }}
                        data-testid="gate-annotation-item"
                    >
                        {mapped && (
                            <span
                                className={`inline-block px-2.5 py-1 rounded-full font-semibold ${
                                    (DESIGN_INTENTS[mapped.intent] || intent).bg
                                } ${(DESIGN_INTENTS[mapped.intent] || intent).text}`}
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    marginBottom: '8px',
                                }}
                            >
                                {mapped.label}
                            </span>
                        )}
                        {reason && (
                            <p
                                data-testid="gate-annotation-reason"
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    color: DS_COLORS.text.primary,
                                    marginTop: mapped ? '8px' : 0,
                                }}
                            >
                                {reason}
                            </p>
                        )}

                        {/* THE SENTENCE. This is the whole point of the record — and where the
                            absent-reason gets the SAME treatment, never a blank.
                            The guard is deliberate: the producer guarantees one of the two is
                            always present, and if that guarantee ever breaks this renders
                            NOTHING rather than an empty labelled box that would read as
                            "we looked and found nothing". */}
                        {(span || absent) && (
                        <>
                        <p
                            className="font-bold"
                            style={{
                                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                color: DS_COLORS.text.secondary,
                                marginTop: '12px',
                            }}
                        >
                            {span ? COPY.quotedLead : COPY.noQuoteLead}
                        </p>
                        {span ? (
                            <blockquote
                                data-testid="gate-annotation-span"
                                className="bg-white rounded-lg"
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    color: DS_COLORS.text.primary,
                                    borderLeft: '4px solid ' + DS_COLORS.border.brand,
                                    padding: '12px',
                                    marginTop: '6px',
                                }}
                            >
                                {span}
                            </blockquote>
                        ) : (
                            <p
                                data-testid="gate-annotation-no-span"
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    color: DS_COLORS.text.secondary,
                                    marginTop: '6px',
                                }}
                            >
                                {absent}
                            </p>
                        )}
                        </>
                        )}

                        {/* WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — Repair/Overrule, rendered
                            only when the server's available_verbs offers them for THIS card.
                            Absent itemId/availableVerbs (every pre-existing caller) renders
                            nothing here, so this is a pure addition. */}
                        <AnnotationVerbControls
                            itemId={itemId}
                            annotationIndex={originalIndex}
                            verbs={availableVerbs}
                            span={span}
                        />
                    </div>
                );
            })}
        </div>
    );
}

GateAnnotationNotice.propTypes = {
    annotations: PropTypes.arrayOf(
        PropTypes.shape({
            failure_class: PropTypes.string,
            reason: PropTypes.string,
            trigger_span: PropTypes.string,
            trigger_span_absent_reason: PropTypes.string,
            annotated_at: PropTypes.string,
        }),
    ),
    // WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — both optional and both default to inert
    // values, so every pre-existing caller (all 12 tests that predate this change, plus
    // both mount sites in LinkedInPostDetail.jsx before this window) renders byte-identical
    // output with no props change required.
    itemId: PropTypes.string,
    availableVerbs: PropTypes.arrayOf(PropTypes.string),
};

export default GateAnnotationNotice;
